// frontend/src/hooks/useSEO.ts
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../locales/languages';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  canonical?: string;
  publicOrigin?: string;
  noindex?: boolean;
}

const DEFAULT_PUBLIC_ORIGIN = 'https://app.xprojex.com';

const upsertMeta = (
  selector: string,
  attribute: 'name' | 'property',
  value: string,
  content?: string,
) => {
  let element = document.querySelector(selector) as HTMLMetaElement | null;

  if (!content) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

export const useSEO = ({
  title,
  description,
  keywords = [],
  ogImage,
  canonical,
  publicOrigin = DEFAULT_PUBLIC_ORIGIN,
  noindex = false,
}: SEOProps = {}) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    const fullTitle = title ? `${title} | xProjex` : 'xProjex | Idea to readiness';
    const canonicalUrl = canonical || `${publicOrigin}${window.location.pathname}`;
    const canonicalPath = (() => {
      try {
        return new URL(canonicalUrl).pathname;
      } catch {
        return window.location.pathname;
      }
    })();
    const socialImage = ogImage || `${publicOrigin}/og-image.png`;

    document.title = fullTitle;
    document.documentElement.lang = lang;
    document.documentElement.dir = 'ltr';

    const metaTags: Array<{
      name?: string;
      property?: string;
      content?: string;
    }> = [
      { name: 'title', content: fullTitle },
      { name: 'description', content: description },
      { name: 'keywords', content: keywords.length > 0 ? keywords.join(', ') : undefined },
      { name: 'language', content: lang },
      { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' },
      { name: 'googlebot', content: noindex ? 'noindex, nofollow' : 'index, follow' },
      { property: 'og:locale', content: lang === 'fr' ? 'fr_CA' : 'en_US' },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: description },
      { property: 'og:image', content: socialImage },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonicalUrl },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: socialImage },
      { name: 'twitter:url', content: canonicalUrl },
    ];

    metaTags.forEach(({ name, property, content }) => {
      const attribute = name ? 'name' : 'property';
      const value = name || property;
      if (!value) return;
      upsertMeta(`meta[${attribute}="${value}"]`, attribute, value, content);
    });

    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    SUPPORTED_LANGUAGES.forEach(({ code: langCode }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', langCode);
      link.setAttribute('href', `${publicOrigin}${canonicalPath}?lang=${langCode}`);
      document.head.appendChild(link);
    });

    const defaultLink = document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('hreflang', 'x-default');
    defaultLink.setAttribute('href', `${publicOrigin}${canonicalPath}`);
    document.head.appendChild(defaultLink);
  }, [i18n.language, title, description, keywords, ogImage, canonical, publicOrigin, noindex]);
};
